import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Icon, Popup } from "semantic-ui-react";

import { AppointmentStatusCell, DATE_FORMATS_FNS, DetailsCard, Pin, ReactTable, useGetPinOptionsDropdownOptions } from "components";
import { usePermanentLocations, usePermanentRealtimeDealers } from "components/DealersAndLocationsDropdown/hooks";
import { PIN_STATUSES, PIN_TYPES } from "models";
import { QuestionResultStatus } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import { useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import "modules/CarDetails/components/PinItems/PinItems.scss";
import { useCarDealer, useCarLocation, usePinItemsQuery } from "modules/CarDetails/hooks";
import { PATHS } from "router/paths";
import { formatDate } from "utils/dateHelperFunctions";

import type { SectionMenu } from "components";
import type { Car, Pin as PinModel } from "models";
import type { ItemWithHistory } from "utils/itemsWithHistory";

type PinItemsProps = {
  car: Car;
  sectionMenu: SectionMenu;
  isExpanded: boolean;
};

const columnHelper = createColumnHelper<ItemWithHistory<PinModel>>();

export const getPinnedItemsCount = (pinnedItems?: ItemWithHistory<PinModel>[]) => pinnedItems?.length ?? 0;

export const PinItems = ({ car, sectionMenu, isExpanded }: PinItemsProps) => {
  const { t } = useTranslation();

  const carDealer = useCarDealer(car);
  const carLocation = useCarLocation(car);
  const { data: pinnedItems = [] } = usePinItemsQuery(car.id);
  const { pinWarrantyOptions } = useGetPinOptionsDropdownOptions(carDealer);
  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const { data: brands = [] } = useCarBrandsQuery();

  if (!carLocation || !sectionMenu.count || !pinnedItems.length) return null;

  const columns = [
    columnHelper.accessor(row => row, {
      id: "date",
      header: t("v8_appointment_date", "Appointment Date"),
      cell: info => formatDate(info.row.original.created_on, DATE_FORMATS_FNS.dateMonthYear),
      size: 185
    }),
    columnHelper.accessor(row => row, {
      id: "appointment",
      header: t("v8_status", "Status"),
      size: 40,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        if (!appointment) return null;

        return (
          <div className="small-status">
            <AppointmentStatusCell value={{ id: appointment.appointment_status_identifier }} statuses={carLocation.statuses} />
          </div>
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "workOrder",
      header: "WO #",
      cell: ({
        row: {
          original: { appointment_id, appointment }
        }
      }) => (
        <Popup
          hoverable
          content={appointment?.wo_nr ?? ""}
          trigger={
            <p className="ellipsis">
              <Link to={`${PATHS.APPOINTMENTS}/${appointment_id}`} target="_blank">
                <Icon className="arrow up right from square" />
                {appointment?.wo_nr}
              </Link>
            </p>
          }
        />
      )
    }),
    columnHelper.accessor(row => row, {
      id: "pinDate",
      header: t("v8_pin_date", "Pin Date"),
      cell: info => formatDate(info.row.original.created_on, DATE_FORMATS_FNS.dateMonthYear)
    }),
    columnHelper.accessor(row => row, {
      id: "pinType",
      header: t("v8_pin_type", "Pin Type"),
      cell: info => PIN_TYPES[info.row.original.pin_type_id] // TODO: this is not ok, we need translated name
    }),
    columnHelper.accessor(row => row, {
      id: "warrantyType",
      header: t("v8_warranty_type", "Warranty Type"),
      cell: ({
        row: {
          original: { warranty_type_id }
        }
      }) => pinWarrantyOptions.find(warranty_type => warranty_type.value === warranty_type_id)?.text
    }),
    columnHelper.accessor(row => row, {
      id: "pinStatus",
      header: t("v8_pin_status", "Pin Status"),
      cell: info => PIN_STATUSES[info.row.original.pin_status_id] // TODO: this is not ok, we need translated name
    }),
    columnHelper.accessor(row => row, {
      id: "support",
      header: `${t("v8_support", "Support")} #`,
      cell: info => info.row.original.support_nr
    }),
    columnHelper.accessor(row => row, {
      id: "claim",
      header: `${t("v8_claim", "Claim")} #`,
      cell: info => info.row.original.claim_nr
    }),
    columnHelper.accessor(row => row, {
      id: "ref",
      header: `${t("v8_ref", "Ref")} #`,
      cell: info => info.row.original.ref_nr
    }),
    columnHelper.accessor(row => row, {
      id: "brand",
      header: t("v8_brand", "Brand"),
      cell: info => info.row.original.appointment?.car_make ?? ""
    }),
    columnHelper.accessor(row => row, {
      id: "intervention",
      header: t("v8_intervention", "Intervention"),
      cell: ({
        row: {
          original: { question_result, intervention }
        }
      }) => (
        <Popup
          hoverable
          content={question_result?.title || intervention?.title}
          trigger={<p className="ellipsis medium">{question_result?.title || intervention?.title}</p>}
        />
      ),
      size: 180
    }),
    columnHelper.accessor(row => row, {
      id: "icons",
      header: "",
      cell: ({ row: { original } }) => {
        const { question_result, intervention, dealer_location_id } = original;
        const locationInfo = locations.find(location => location.id === dealer_location_id);
        return (
          <div className="action-container">
            {(question_result?.status || intervention?.question_result_status) && (
              <div className="pin-status">
                <QuestionResultStatus status={intervention?.question_result_status || question_result?.status} />
              </div>
            )}
            <div className="pin-type">
              <Pin pin={original} location={carLocation} vin={car.vin_nr} brands={brands} />
            </div>
            <Popup
              hoverable
              content={
                <div className="location-info-popup">
                  <p>{dealers.find(location => location.id === locationInfo?.dealer_id)?.name}</p>
                  <p> {locationInfo?.name || ""}</p>
                </div>
              }
              header={`${t("v8_dealer_location", "Dealer/Location")}`}
              trigger={
                <Button className="tiLightPurple af" color="orange">
                  <Icon className="house chimney" />
                </Button>
              }
            />
          </div>
        );
      }
    })
  ];

  return (
    <DetailsCard isExpanded={isExpanded} sectionMenuId={sectionMenu.id} icon={sectionMenu.icon} title={`${sectionMenu.title} (${sectionMenu.count})`}>
      <div className="PinItems">
        <ReactTable columns={columns} data={pinnedItems} />
      </div>
    </DetailsCard>
  );
};
